// frontend/src/utils/use-plan-guard.ts
import { useMemo } from "react";
import { FREE_MAX_PROJECTS, canUse, type Plan, type FeatureKey } from "@/utils/plan-capabilities";
import { useAuthContext } from "@/context/auth-context";

/**
 * Frontend-only guard for plan limits and feature gating.
 * Accepts an optional projectCount so you can use it from pages that already
 * fetched the projects list (e.g., Dashboard). If omitted, you can still use
 * the feature gating via `can(feature)`, but canAddProject will assume 0.
 */
export function usePlanGuard(projectCountOverride?: number) {
  const { user } = useAuthContext();
  const plan = (user?.plan ?? "free") as Plan;

  const projectCount = Number.isFinite(projectCountOverride!)
    ? (projectCountOverride as number)
    : 0; // explicit is better than guessing

  const canAddProject = useMemo(() => {
    if (plan !== "free") return true;
    return projectCount < FREE_MAX_PROJECTS;
  }, [plan, projectCount]);

  const can = (feature: FeatureKey) => canUse(plan, feature);

  return {
    plan,
    projectCount,
    canAddProject,
    FREE_MAX_PROJECTS,
    can,
  };
}

/** Pure helper (no React) for unit tests and non-React consumers */
export function getCanAddProject(plan: Plan, projectCount: number) {
  if (plan !== "free") return true;
  return projectCount < FREE_MAX_PROJECTS;
}